import { useState, FormEvent, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSharedState } from "state";
import ModalComponent from "components/modal";
import Select, { components, GroupHeadingProps, GroupProps, MultiValue, SingleValue } from "react-select";
import { addReportTemplate, updateReportTemplate } from "api/budget_request";
import { IconArrowNarrowRight, IconInfoCircle, IconX } from "@tabler/icons-react";

type SelectOption = {
  level: number;
  value: number;
  label: string;
  disabled: boolean;
};

type StringOption = {
  value: string;
  label: string;
};

type NumberOption = {
  value: number;
  label: string;
};

type LevelOption = {
  label: string;
  value: number;
  level: number;
  disabled: boolean;
  options: {
    label: string;
    value: number | null;
    level: number | null;
    disabled: boolean;
  }[];
};

export default function NewReportTemplate() {
  const {
    levels,
    getLevelsData,
    formulas,
    getFormulasData,
    notify,
    reportTemplates,
    getReportTemplatesData,
    budgetColumns,
    budgetForecasts,
  } = useSharedState();
  const { templateId } = useParams();

  const [reportName, setReportName] = useState<string>("");
  const [budgets, setBudgets] = useState<SelectOption[]>([
    { label: "Выберите бюджет", value: 0, level: 0, disabled: false },
  ]);
  const [budgetLevels, setBudgetLevels] = useState<SelectOption[][]>([]);

  const [isHierarchical, setHierarchical] = useState<boolean>(true);
  const [isForecast, setForecast] = useState<boolean>(false);
  const [selectedColumns, setSelectedColumns] = useState<StringOption[]>([]);
  const [selectedForecasts, setSelectedForecasts] = useState<StringOption[]>([]);
  const [selectedFormulas, setSelectedFormulas] = useState<NumberOption[]>([]);

  const [isFormLoading, setFormLoading] = useState<boolean>(false);
  const [isFormSubmitted, setFormSubmitted] = useState<boolean>(false);

  const zerothLevels = useMemo(() => {
    return levels.filter((lvl) => lvl.parent_budget_type_id === null);
  }, [levels]);

  const validateForecastConditions = useMemo(() => {
    // Return false if budgets is empty
    if (!budgets.length || budgets[0].value === 0) {
      return false;
    }

    // Get forecast_min_level values for each budget
    const forecastLevels = budgets.map((budget) => {
      const budgetType = zerothLevels.find((level) => level.budget_type_id === budget.value);
      return budgetType?.forecast_min_level;
    });

    // Check if all budgets have the same valid forecast_min_level
    const firstForecastLevel = forecastLevels[0];
    if (!firstForecastLevel || !forecastLevels.every((level) => level === firstForecastLevel)) {
      return false;
    }

    // Check if budgetLevels have the required levels for each budget
    const hasValidLevels = budgets.every((budget, index) => {
      const budgetLevelNumbers = budgetLevels[index]?.map((level) => level.level) || [];

      // Check if there are no excessive levels (above forecast_min_level)
      if (budgetLevelNumbers.some((num) => num > firstForecastLevel)) {
        return false;
      }

      // Check if all required levels are present
      for (let i = 1; i <= firstForecastLevel; i++) {
        if (!budgetLevelNumbers.includes(i)) {
          return false;
        }
      }
      return true;
    });

    // Check if selectedColumns only contains parent_budget_type and level columns
    const validColumnTypes = [
      "parent_budget_type",
      ...Array.from({ length: firstForecastLevel }, (_, index) => index + 1).map((lvl) => `lvl${lvl}`),
    ];
    const hasValidColumns = validColumnTypes.every((col) =>
      selectedColumns.find((selColumn) => selColumn.value === col)
    );

    return hasValidLevels && hasValidColumns;
  }, [budgets, zerothLevels, budgetLevels, selectedColumns]);

  // Effect to handle isForecast state based on conditions
  useEffect(() => {
    if (!validateForecastConditions && isForecast) {
      setForecast(false);
      setSelectedForecasts([]);
    }
  }, [validateForecastConditions, isForecast]);

  const filteredZerothLevels = useMemo(() => {
    return zerothLevels.filter(
      (lvl) => lvl.budget_type_id !== budgets.find((b) => b.value === lvl.budget_type_id)?.value
    );
  }, [zerothLevels, budgets]);

  const filteredLevels = useMemo(() => {
    return (zerothLevelId: number) => levels.filter((lvl) => lvl.parent_budget_type_id === zerothLevelId);
  }, [levels]);

  const groupedOptions = useMemo(() => {
    return (budgetIndex: number): LevelOption[] =>
      filteredLevels(budgets[budgetIndex]?.value)
        .map((budget) => budget.level_number)
        .filter((value, index, array) => array.indexOf(value) === index)
        .sort((a, b) => a! - b!)
        .map((levelNumber) => ({
          label: `Уровень ${levelNumber}`,
          value: levelNumber!,
          level: levelNumber!,
          disabled: false,
          options: levels
            .filter(
              (lvl) => lvl.parent_budget_type_id === budgets[budgetIndex].value && lvl.level_number === levelNumber
            )
            .map((l) => ({
              label: `${l.budget_type} (ур. ${l.level_number})`,
              value: l.budget_type_id,
              level: l.level_number,
              disabled: !!!l.status,
            })),
        }));
  }, [filteredLevels, budgets, levels]);

  const levelOptions = useMemo(() => {
    return budgetLevels
      .flat()
      .map((budget) => budget.level)
      .filter((value, index, array) => array.indexOf(value) === index)
      .map((l) => ({
        label: `Уровень ${l}`,
        value: `lvl${l}`,
      }));
  }, [budgetLevels]);

  const formulaOptions: NumberOption[] = useMemo(() => {
    return formulas.map((formula) => ({
      label: formula.formula_name,
      value: formula.budget_formula_id,
    }));
  }, [formulas]);

  useEffect(() => {
    getLevelsData();
    getFormulasData();
  }, []);

  useEffect(() => {
    if (templateId) {
      const template = reportTemplates.find((t) => t.budget_report_template_id === +templateId);

      setReportName(template?.template_name || "");
      setBudgets(
        template?.json_description.groupings.map((budget, budgetIndex) => ({
          label: zerothLevels.find((zero) => zero.budget_type_id === budget.parent_budget_type_id)?.budget_type || "",
          value: budget.parent_budget_type_id,
          level: 0,
          disabled: !!!zerothLevels.find((zero) => zero.budget_type_id === budget.parent_budget_type_id)?.status,
        })) || [{ label: "", value: 0, level: 0, disabled: false }]
      );
      setBudgetLevels(
        template?.json_description.groupings.map((budget) =>
          Object.entries(budget.levels)
            .map(([level, values]) =>
              values.map((value: number) => ({
                label: levels.find((l) => l.budget_type_id === value)?.budget_type + ` (ур. ${level})` || "",
                value: value,
                level: parseInt(level),
                disabled: !!!levels.find((l) => l.budget_type_id === value)?.status,
              }))
            )
            .flat()
        ) || [
          [
            {
              label: "",
              value: 0,
              level: 0,
              disabled: false,
            },
          ],
        ]
      );
      setHierarchical(template?.json_description.structure === "hierarchical" ? true : false);
      setForecast(template?.json_description.add_forecast || false);
      setSelectedColumns(
        template?.json_description.need_columns
          .filter((c) => !c.includes("mon"))
          .map((c) => ({ label: budgetColumns.find((col) => col.value === c)?.label || c, value: c }))
          .concat(
            template?.json_description.need_columns.some((c) => c.includes("mon"))
              ? [{ value: "all_month", label: "Все месяцы" }]
              : []
          ) || []
      );
      setSelectedFormulas(
        template?.json_description.additional_formulas?.map((f) => ({
          label: formulas.find((formula) => formula.budget_formula_id === f)?.formula_name || "",
          value: f,
        })) || []
      );
      setSelectedForecasts(
        template?.json_description.need_formulas?.map((f) => ({
          label: budgetForecasts.find((forecast) => forecast.value === f)?.label || f,
          value: f,
        })) || []
      );
    }
  }, [templateId, reportTemplates, levels]);

  const handleColumnChange = (newValue: MultiValue<StringOption>) => {
    setSelectedColumns(newValue as StringOption[]);
  };

  const handleFormulaChange = (newValue: MultiValue<NumberOption>) => {
    setSelectedFormulas(newValue as NumberOption[]);
  };

  const handleForecastChange = (newValue: MultiValue<StringOption>) => {
    setSelectedForecasts(newValue as StringOption[]);
  };

  const submitBudget = async (e: FormEvent) => {
    e.preventDefault();

    setFormLoading(true);
    try {
      const payload = {
        template_name: reportName,
        json_description: {
          structure: isHierarchical ? "hierarchical" : "tabular",
          add_forecast: isForecast,
          groupings: budgets.map((budget, budgetIndex) => ({
            levels: Object.fromEntries(
              budgetLevels[budgetIndex]
                .map((lvl) => lvl.level)
                .filter((value, index, array) => array.indexOf(value) === index)
                .map((lvl) => [lvl, budgetLevels[budgetIndex].filter((l) => l.level === lvl).map((l) => l.value)])
            ),
            parent_budget_type_id: budget.value,
          })),
          need_columns: (() => {
            const specialColumns = ["all_month", "row_total", "quantity"];
            const normalColumns = selectedColumns.filter((c) => !specialColumns.includes(c.value));

            let result: string[] = [];

            // Preserve original order for special columns
            selectedColumns.forEach((col) => {
              if (specialColumns.includes(col.value)) {
                if (col.value === "all_month") {
                  result.push(...budgetColumns.filter((c) => c.value.includes("mon")).map((c) => c.value));
                } else {
                  result.push(col.value);
                }
              }
            });

            // Add normal columns
            result.unshift(...normalColumns.map((c) => c.value));

            return result;
          })(),
          additional_formulas: selectedFormulas.map((f) => f.value),
          need_formulas: selectedForecasts.map((f) => f.value),
        },
      };

      await (templateId ? updateReportTemplate(+templateId, payload) : addReportTemplate(payload));
      getReportTemplatesData();
      notify("Отчёт сохранён", "success");

      setFormSubmitted(true);
    } catch (error: any) {
      let err = JSON.stringify(error?.response?.data?.detail);
      notify(err, "danger", templateId ? "Ошибка при сохранении отчёта" : "Ошибка при сохранении отчёта");
    } finally {
      setFormLoading(false);
    }
  };

  const handleBudgetInputChange = (rowIndex: number, value: SingleValue<SelectOption>) => {
    setBudgets((prev) =>
      prev.map((row, index) =>
        index === rowIndex
          ? {
              label: value?.label || "",
              value: value?.value || 0,
              level: value?.level || 0,
              disabled: value?.disabled || false,
            }
          : row
      )
    );

    setBudgetLevels((prev) => {
      const newLevels = [...prev];
      newLevels[rowIndex] = [];
      return newLevels;
    });
  };

  const handleBudgetLevelInputChange = (rowIndex: number, value: MultiValue<SelectOption>) => {
    const selectedValues = value.map((v) => ({
      label: v.label,
      value: v.value,
      level: v.level || 0,
      disabled: v.disabled,
    }));

    setBudgetLevels((prev) => {
      const newLevels = [...prev];
      newLevels[rowIndex] = selectedValues;
      return newLevels;
    });
  };

  const addWholeLevel = (rowIndex: number, value: MultiValue<SelectOption>) => {
    setBudgetLevels((prev) => {
      const currentLevels = prev[rowIndex] || [];
      const uniqueValues = [...currentLevels, ...value].filter(
        (item, index, self) => index === self.findIndex((t) => t.value === item.value)
      );

      const newLevels = [...prev];
      newLevels[rowIndex] = uniqueValues;
      return newLevels;
    });
  };

  const addRowInput = () => {
    setBudgets((prev) => [...prev, { label: "Выберите бюджет", value: 0, level: 0, disabled: false }]);
  };
  const deleteRow = (index: number) => {
    setBudgets(budgets.filter((b, i) => i !== index));
    setBudgetLevels(budgetLevels.filter((b, i) => i !== index));
  };

  const groupStyles = {
    border: `2px dotted green`,
    borderRadius: "5px",
    background: "#f2fcff",
  };

  const Group = (props: GroupProps<SelectOption, true>) => (
    <div style={groupStyles}>
      <components.Group {...props} />
    </div>
  );

  const renderBudgets = () => (
    <>
      {budgets.map((row, budgetIndex) => (
        <div className="d-flex align-items-center gap-2" key={budgetIndex}>
          <div className="col">
            <div className="mb-3">
              <label className="form-label required">{"Тип бюджета"}</label>
              <div className="input-icon mb-3">
                <Select<SelectOption>
                  classNamePrefix={"react-select"}
                  name={"budget_type_ids"}
                  placeholder={"Выберите бюджет"}
                  value={row}
                  onChange={(newValue) => handleBudgetInputChange(budgetIndex, newValue)}
                  isDisabled={isFormLoading}
                  options={filteredZerothLevels.map((lvl) => ({
                    label: lvl.budget_type!,
                    value: lvl.budget_type_id!,
                    level: lvl.level_number!,
                    disabled: !!!lvl.status,
                  }))}
                  isOptionDisabled={(option) => option.disabled}
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </div>
          </div>

          <div className="col-auto" style={{ paddingTop: 12 }}>
            <IconArrowNarrowRight size={32} />
          </div>

          <div className="col">
            <div className="mb-3">
              <label className="form-label">{"Уровни бюджета"}</label>
              <div className="input-icon mb-3">
                <Select<SelectOption, true>
                  classNamePrefix={"react-select"}
                  value={budgetLevels[budgetIndex] || []}
                  onChange={(newValue) => handleBudgetLevelInputChange(budgetIndex, newValue)}
                  isDisabled={isFormLoading}
                  options={groupedOptions(budgetIndex)}
                  closeMenuOnSelect={false}
                  components={{
                    Group,
                    GroupHeading: (props) => (
                      <div className="d-flex align-items-center justify-content-between">
                        <components.GroupHeading {...props} />
                        <div
                          className="select-group-header-btn"
                          onClick={() => addWholeLevel(budgetIndex, props.data.options as any)}
                        >
                          Добавить все
                        </div>
                      </div>
                    ),
                  }}
                  isOptionDisabled={(option) => option.disabled}
                  isMulti
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                  }}
                />
              </div>
            </div>
          </div>

          <div className="col-auto text-danger" style={{ paddingTop: 12 }}>
            <div title="Удалить бюджет" data-bs-toggle="tooltip" data-bs-placement="top">
              <IconX className="cursor-pointer" onClick={() => deleteRow(budgetIndex)} />
            </div>
          </div>
        </div>
      ))}

      <div className="mb-3">
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={() => addRowInput()}
          disabled={isFormLoading || budgets.length >= zerothLevels.length}
        >
          + Добавить бюджет
        </button>
      </div>
    </>
  );

  const newBudgetBody = (
    <form id="budgetForm" className="row" onSubmit={submitBudget}>
      <div className="col-lg-12">
        <div className="mb-3">
          <label className="form-label required">Название отчёта</label>
          <div className="input-icon mb-3">
            <input
              type="text"
              className="form-control rounded-2xl"
              name="template_name"
              value={reportName}
              onChange={(e) => setReportName(e.target.value.slice(0, 100))}
              disabled={isFormLoading}
            />
          </div>
        </div>
      </div>

      {renderBudgets()}

      <div className="col-lg-12">
        <div className="mb-3">
          <label className="form-label required">Колонки для отчёта</label>
          <div className="input-icon mb-3">
            <Select
              classNamePrefix={"react-select"}
              placeholder={"Выберите колонки"}
              value={selectedColumns}
              onChange={(newValue) => handleColumnChange(newValue)}
              options={[
                ...budgetColumns.filter(
                  (c) => !c.value.includes("mon") && !c.value.includes("lvl") && c.value !== "company_name"
                ),
                { value: "all_month", label: "Все месяцы" },
                ...levelOptions,
              ]}
              isDisabled={isFormLoading}
              closeMenuOnSelect={false}
              isSearchable
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
        </div>
      </div>

      <div className="col-lg-12">
        <div className="mb-3">
          <label className="form-label">Формулы для отчёта</label>
          <div className="input-icon mb-3">
            <Select
              classNamePrefix={"react-select"}
              placeholder={"Выберите формулы"}
              value={selectedFormulas}
              onChange={(newValue) => handleFormulaChange(newValue)}
              options={formulaOptions}
              isDisabled={isFormLoading}
              closeMenuOnSelect={false}
              isSearchable
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
        </div>
      </div>

      <div className="d-flex gap-3 mb-3">
        <div className="col-auto">
          <div className="form-label required">Иерархия</div>
          <div>
            <label className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="radios-inline"
                checked={isHierarchical}
                onChange={(e) => setHierarchical(e.target.checked)}
              />
              <span className="form-check-label">Да</span>
            </label>
            <label className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="radios-inline"
                checked={!isHierarchical}
                onChange={(e) => setHierarchical(!e.target.checked)}
              />
              <span className="form-check-label">Нет</span>
            </label>
          </div>
        </div>

        <div className="col-auto">
          <div className="form-label required">Прогноз</div>
          <div>
            <label className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="forecast-radios"
                checked={isForecast}
                onChange={(e) => setForecast(e.target.checked)}
                disabled={!validateForecastConditions}
              />
              <span className="form-check-label">Да</span>
            </label>
            <label className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="forecast-radios"
                checked={!isForecast}
                onChange={(e) => setForecast(!e.target.checked)}
                disabled={!validateForecastConditions}
              />
              <span className="form-check-label">Нет</span>
            </label>
          </div>
        </div>
      </div>

      {isForecast && (
        <div className="col-lg-12">
          <div className="mb-3">
            <label className="form-label">Формулы для прогноза</label>
            <div className="input-icon mb-3">
              <Select
                classNamePrefix={"react-select"}
                placeholder={"Выберите формулы"}
                value={selectedForecasts}
                onChange={(newValue) => handleForecastChange(newValue)}
                options={budgetForecasts}
                isDisabled={isFormLoading}
                closeMenuOnSelect={false}
                isSearchable
                isMulti
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="alert alert-primary" role="alert">
        <div className="d-flex">
          <IconInfoCircle className="icon alert-icon" />
          <div>
            <h4 className="alert-title">Внимание! Прогноз доступен только в следующих случаях:</h4>
            <div className="text-secondary">
              1. Для выбранного типа бюджета должны быть выбраны как минимум по одному бюджету из каждого уровня,
              соответствующие уровню прогноза;
              <br />
              2. Если выбраны несколько типов бюджетов - уровни прогноза должны быть одинаковыми;
              <br />
              3. При выборе колонок для отчёта - обязательно должны быть выбраны: "Тип бюджета", уровни (в порядке от
              меньшего к большему). При выборе колонок в ином порядке прогноз не будет доступен для формирования.
              <br />
              {/* {budgets.length ? (
                <>
                  Уровни прогноза выбранных типов бюджета:
                  <br />
                  {budgets.map((budget, index) =>
                    budget.value ? (
                      <span key={index}>
                        {budget.label}:{" "}
                        {levels.find((l) => l.budget_type_id === budget.value)?.forecast_min_level || "Без прогноза"}
                        <br />
                      </span>
                    ) : undefined
                  )}
                </>
              ) : undefined} */}
            </div>
          </div>
        </div>
      </div>
    </form>
  );

  const newBudgetFooter = (
    <button type="submit" form="budgetForm" className="btn btn-primary d-flex gap-1" disabled={isFormLoading}>
      {templateId ? "Сохранить изменения" : "Создать отчёт"}
      {isFormLoading && <div className="spinner-border spinner-border-sm" role="status" />}
    </button>
  );

  return (
    <ModalComponent
      header={templateId ? "Редактирование отчёта" : "Новый отчёт"}
      body={newBudgetBody}
      footer={newBudgetFooter}
      disableClose={isFormLoading}
      shouldModalClose={isFormSubmitted}
    />
  );
}
